'use client';

import type { ColumnDef } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { systemLogQueries } from '../services/system-logs.queries';
import { ActionIcon, getActionColor } from '../utils/action-style';
import { useLogFormatters } from '../utils/use-log-formatters';
import type { AuditLog } from '../types';

export function useSystemLogsColumns(): ColumnDef<AuditLog>[] {
  const t = useTranslations('systemLogs');
  const { translateAction, translateSubjectType } = useLogFormatters();
  const { data: filtersData } = useQuery(systemLogQueries.filters());

  return useMemo<ColumnDef<AuditLog>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'user',
        accessorFn: (row) => row.user?.name ?? '',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('user')} />
        ),
        cell: ({ row }) => {
          const user = row.original.user;
          if (!user) {
            return (
              <span className='text-muted-foreground text-sm'>
                {t('systemAction')}
              </span>
            );
          }
          return (
            <div className='flex items-center gap-2'>
              <div className='bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium'>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className='min-w-0'>
                <p className='truncate text-sm font-medium'>{user.name}</p>
                <p className='text-muted-foreground truncate text-xs'>
                  {user.email}
                </p>
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('user'),
          variant: 'infiniteMultiSelect' as const,
          icon: 'solar:user-bold-duotone',
          permission: 'system_logs.column.user'
        }
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('action')} />
        ),
        cell: ({ row }) => {
          const action = row.original.action;
          return (
            <Badge
              variant='secondary'
              className={cn(
                'gap-1 text-xs font-medium',
                getActionColor(action)
              )}
            >
              <ActionIcon action={action} className='h-3 w-3' />
              {translateAction(action)}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('action'),
          variant: 'multiSelect' as const,
          icon: 'solar:bolt-bold-duotone',
          options: (filtersData?.data?.actions ?? []).map((a: string) => ({
            label: translateAction(a),
            value: a
          })),
          permission: 'system_logs.column.action'
        }
      },
      {
        id: 'subject_type',
        accessorKey: 'subject_type',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('subjectType')} />
        ),
        cell: ({ row }) => {
          const { subject_type, subject_id, subject_name } = row.original;
          if (!subject_type)
            return <span className='text-muted-foreground text-sm'>—</span>;
          const label = translateSubjectType(subject_type);
          return (
            <span className='text-muted-foreground text-sm'>
              {subject_name
                ? `${subject_name} (${label})`
                : `${label}${subject_id ? ` #${subject_id}` : ''}`}
            </span>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('subjectType'),
          variant: 'multiSelect' as const,
          icon: 'solar:layers-minimalistic-bold-duotone',
          options: (filtersData?.data?.subject_types ?? []).map(
            (s: string) => ({
              label: translateSubjectType(s),
              value: s
            })
          ),
          permission: 'system_logs.column.subjectType'
        }
      },
      {
        id: 'details',
        accessorKey: 'meta',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('details')} />
        ),
        cell: ({ row }) => {
          const meta = row.original.meta;
          if (!meta) return <span className='text-muted-foreground'>—</span>;

          const permissions = meta.permissions as string[] | undefined;
          if (permissions && permissions.length > 0) {
            const displayed = permissions.slice(0, 3);
            const remaining = permissions.length - displayed.length;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex max-w-[200px] flex-wrap gap-1'>
                    {displayed.map((p) => (
                      <Badge
                        key={p}
                        variant='outline'
                        className='text-[10px] font-normal'
                      >
                        {p}
                      </Badge>
                    ))}
                    {remaining > 0 && (
                      <Badge
                        variant='secondary'
                        className='text-[10px] font-normal'
                      >
                        +{remaining}
                      </Badge>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side='bottom' className='max-w-[300px]'>
                  <p className='mb-1 text-xs font-medium'>
                    {t('permissionsAssigned')} ({permissions.length})
                  </p>
                  <div className='flex flex-wrap gap-1'>
                    {permissions.map((p) => (
                      <Badge
                        key={p}
                        variant='outline'
                        className='text-[10px] font-normal'
                      >
                        {p}
                      </Badge>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          }

          // Display other meta keys as simple text
          const entries = Object.entries(meta).filter(
            ([key]) => key !== 'permissions' && key !== 'permissions_count'
          );
          if (entries.length === 0)
            return <span className='text-muted-foreground'>—</span>;

          return (
            <div className='text-muted-foreground text-xs'>
              {entries.map(([key, value]) => (
                <span key={key}>
                  {key}: {String(value)}
                </span>
              ))}
            </div>
          );
        },
        enableSorting: false,
        meta: { label: t('details'), permission: 'system_logs.column.details' }
      },
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('timestamp')} />
        ),
        cell: ({ row }) => {
          const date = new Date(row.original.created_at);
          return (
            <span className='text-muted-foreground text-sm'>
              {date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}{' '}
              {date.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('timestamp'),
          variant: 'dateTimeRange' as const,
          permission: 'system_logs.column.timestamp'
        }
      }
    ],
    [t, translateAction, translateSubjectType, filtersData]
  );
}
